/**
 * Offline fixture for the v2 field catalog and field detail (Storybook /
 * local dev). The catalog mirrors Figma 6433:559545. Figma repeats "State"
 * under Geographic — the second row is "City" here, which is what the design
 * intends. Every `kind` is present, `channel` being the degenerate dimension.
 */
import { mockNotFoundError } from "../mockDomainError";
import type {
  FieldCatalogParams,
  FieldCatalogResponseApi,
  FieldDetailReadApi,
  FieldSummaryApi,
} from "./chartStudioDataModel.types";
import type { ModelFieldKindApi } from "../storyCanvas/storyCanvas.types";
import type { FilterValueType } from "../storyCanvas/storyFilters.types";
import type { DataModelFieldCategory } from "../../components/registry/dataModelFieldSections";

/** The release the mock story's editable chart is built on (its `query_model`). */
export const MOCK_MODEL_RELEASE_ID = "dm-superstore";

const entry = (id: string, name: string, kind: ModelFieldKindApi): FieldSummaryApi => ({
  id,
  kind,
  name,
});

export const MOCK_DATA_MODEL_FIELDS_RESPONSE: FieldCatalogResponseApi = {
  dates: [
    entry("order_date", "Order date", "dimension_attribute"),
    entry("order_time", "Order time", "dimension_attribute"),
  ],
  dimensions: [
    entry("region", "Region", "dimension_attribute"),
    entry("store", "Store", "dimension_attribute"),
    entry("category", "Category", "dimension_attribute"),
    entry("product", "Product", "dimension_attribute"),
    entry("segment", "Segment", "dimension_attribute"),
    entry("channel", "Channel", "degenerate_dimension"),
  ],
  geographic: [
    entry("state", "State", "dimension_attribute"),
    entry("city", "City", "dimension_attribute"),
  ],
  measures: [
    entry("sales", "Sales", "measure"),
    entry("quantity", "Quantity", "measure"),
    entry("profit", "Profit", "measure"),
    entry("discount", "Discount", "measure"),
  ],
  calculated: [
    entry("margin_pct", "Margin %", "measure"),
    entry("revenue_per_unit", "Revenue / Unit", "measure"),
  ],
};

/** What the filter pickers and `/filters` writes read: dates are temporal, measures continuous, the rest categorical. */
export const MOCK_VALUE_TYPE_BY_CATEGORY: Record<DataModelFieldCategory, FilterValueType> = {
  dates: "temporal",
  dimensions: "categorical",
  geographic: "categorical",
  measures: "continuous",
  calculated: "continuous",
};

const releaseNotFound = (modelReleaseId: string) =>
  mockNotFoundError(`Model release not found: ${modelReleaseId}`);

/**
 * Stands in for the server, including its 404 for an unknown release. Search
 * is matched here the way the backend will match it, so the UI can be
 * exercised offline; the UI itself never filters.
 */
export async function mockFetchFieldCatalog(
  modelReleaseId: string,
  params: FieldCatalogParams = {},
): Promise<FieldCatalogResponseApi> {
  if (modelReleaseId !== MOCK_MODEL_RELEASE_ID) throw releaseNotFound(modelReleaseId);
  const needle = params.search?.trim().toLowerCase() ?? "";
  if (!needle) return MOCK_DATA_MODEL_FIELDS_RESPONSE;
  const matching = (fields: FieldSummaryApi[]) =>
    fields.filter(
      (field) =>
        field.name.toLowerCase().includes(needle) || field.id.toLowerCase().includes(needle),
    );
  return {
    dates: matching(MOCK_DATA_MODEL_FIELDS_RESPONSE.dates),
    dimensions: matching(MOCK_DATA_MODEL_FIELDS_RESPONSE.dimensions),
    geographic: matching(MOCK_DATA_MODEL_FIELDS_RESPONSE.geographic),
    measures: matching(MOCK_DATA_MODEL_FIELDS_RESPONSE.measures),
    calculated: matching(MOCK_DATA_MODEL_FIELDS_RESPONSE.calculated),
  };
}

/* ─── Field detail ──────────────────────────────────────────────────────── */

const detail = (
  field: FieldSummaryApi,
  completeness: number,
  unique: number,
  most_common: string,
): FieldDetailReadApi => ({ ...field, completeness, total: 900, unique, most_common });

const byId = Object.fromEntries(
  Object.values(MOCK_DATA_MODEL_FIELDS_RESPONSE)
    .flat()
    .map((field) => [field.id, field]),
) as Record<string, FieldSummaryApi>;

/**
 * One entry per catalog field, keyed by id. "Order date" matches Figma
 * 6433:560623 exactly. The rest cover the edge cases: partial completeness
 * with an extra decimal (margin_pct), no most common value (blank string,
 * since the spec makes it a required string), 0% completeness, and a long
 * value (product).
 */
export const MOCK_FIELD_STATS: Record<string, FieldDetailReadApi> = {
  order_date: detail(byId.order_date, 1, 317, "04-08-2025"),
  order_time: detail(byId.order_time, 1, 288, "10:30"),
  region: detail(byId.region, 1, 4, "West"),
  store: detail(byId.store, 0.982, 42, "Downtown Flagship"),
  category: detail(byId.category, 1, 3, "Office Supplies"),
  product: detail(
    byId.product,
    0.996,
    612,
    "Hon 5400 Series Task Chairs for Big and Tall, Black Leather",
  ),
  segment: detail(byId.segment, 1, 3, "Consumer"),
  channel: detail(byId.channel, 0.91, 4, "Online"),
  state: detail(byId.state, 1, 49, "California"),
  city: detail(byId.city, 0.964, 531, "New York City"),
  sales: detail(byId.sales, 1, 874, "19.44"),
  quantity: detail(byId.quantity, 1, 14, "3"),
  profit: detail(byId.profit, 1, 861, "0"),
  discount: detail(byId.discount, 0.824, 12, "0.2"),
  margin_pct: detail(byId.margin_pct, 0.6425, 900, ""),
  revenue_per_unit: detail(byId.revenue_per_unit, 0, 0, ""),
};

/** Stands in for the server, including its 404s for an unknown release or field. */
export async function mockFetchChartStudioFieldStats(
  modelReleaseId: string,
  fieldId: string,
): Promise<FieldDetailReadApi> {
  if (modelReleaseId !== MOCK_MODEL_RELEASE_ID) throw releaseNotFound(modelReleaseId);
  const hit = MOCK_FIELD_STATS[fieldId];
  if (!hit) throw mockNotFoundError(`Field not found: ${fieldId}`);
  return hit;
}
